export const transformInvestmentsByCompanyForTable = (investmentsByCompany, expandedCompanies) => {
  const rows = Object.keys(investmentsByCompany).reduce((rows, companyName) => {
    const companyData = investmentsByCompany[companyName]
    const companyRow = {
      issuerName: companyName,
      amount: companyData.totalInvestments,
      number_of_units: companyData.totalShares,
      type: 'company',
      companyName,
      investments: companyData.investments,
      companyDocs: companyData.companyDocs,
      // currently assuming that any company will always have
      // all offerings using the same currency
      currency: companyData.investments[0] ? companyData.investments[0].currency : null,
    }
    const investmentRows = companyData.investments.map(investment => ({
      ...investment,
      companyName,
      issuerName: `${companyName}`,
      offeringName: generateOfferingName(investment),
      isVisible: expandedCompanies[companyName],  
    }))
    return [...rows, companyRow, ...investmentRows]
  }, [])
  return rows
}

export const generateOfferingName = (investment = {}) => {
  return `${investment.issuerName ?? ""} ${investment.offering_id  ?? ""}`
}

export const sortInvestmentsByCompany = (investmentData) => {
  const investments = [...investmentData];
  const investmentsByCompany = investments.reduce((investmentsByCompany, investment) => {
    const companyData = investmentsByCompany[investment.companyName] || {}
    investmentsByCompany[investment.companyName] = {
      ...companyData,
      investments: [...(companyData.investments || []), investment]
    }
    return investmentsByCompany
  }, {})
  return investmentsByCompany
}

export const filterCompanyDocs = (investmentsByCompany) => {
  const investmentsByCompanyData = JSON.parse(JSON.stringify({ ...investmentsByCompany }));
  for (const company in investmentsByCompanyData){
    const companyDocsById = {}
    for(const currentInvestment in investmentsByCompanyData[company]['investments']) {
      const companyDocsForInvestment = investmentsByCompanyData[company]['investments'][currentInvestment].documents.filter(document => document.is_personal === 0);
        for(const newDoc in companyDocsForInvestment) {
          const document = companyDocsForInvestment[newDoc]
          companyDocsById[document.id] = document
          investmentsByCompanyData[company]['investments'][currentInvestment].documents = investmentsByCompanyData[company]['investments'][currentInvestment].documents.filter(document => {
            // just remove docs with tag "updates" since 
            // offering-doc also needs be shown in each investment row
            return document.tag !== "updates";
          });
        }  
      }
      investmentsByCompanyData[company]['companyDocs'] = Object.keys(companyDocsById).map(id => companyDocsById[id]).filter(doc => doc.tag !== "offering-doc");
  }
  return investmentsByCompanyData;
}

export const getTotalsByCompany = (investmentData, useCompanyRows = false) => {
  const totalsByCompany = investmentData.reduce((totalsByCompany, investment) => {
    if ((investment.type === 'company' && !useCompanyRows) || (investment.type !== 'company' && useCompanyRows)) {
      return totalsByCompany
    }
    const companyData = totalsByCompany[investment.companyName] || {}
    totalsByCompany[investment.companyName] = {
      ...companyData,
      totalInvestments: (companyData.totalInvestments || 0) + Number(investment.amount),
      totalShares: (companyData.totalShares || 0) + Number(investment.number_of_units),
    }
    return totalsByCompany
  }, {})
  return totalsByCompany
}

export const isInvestmentCompanyInSearch = (investments, search) => {
  return investments.some(investmentInCompany => {
    return investmentInCompany.issuerName.toLowerCase().includes(search.toLowerCase())
  })
}

export const doesCompanyOfferingHaveInvestmentOfferingName = (investments, search) => {
  return investments.filter(investment => investment.type !== 'company').some(investmentInCompany => {
    return investmentInCompany.offeringName.toLowerCase().includes(search.toLowerCase())
  })
}

export const formatCurrency = (number) => {
  const parsedNumber = Number(number)
  if (isNaN(parsedNumber)) return number
  return parsedNumber.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export const formatNumberLocale = (number) => {
  const parsedNumber = Number(number)
  if (isNaN(parsedNumber)) return number
  return parsedNumber.toLocaleString('en-US')
}

export const filterInvestmentsByName = (investmentData, name) => {
  return investmentData.filter(investment => {
    const matchesInvestmentName = investment.issuerName.toLowerCase().includes(name.toLowerCase())
    const matchesCompanyName = investment.companyName.toLowerCase().includes(name.toLowerCase())
    const isCompanyRowWithMatchingInvestments = investment.type === 'company'
      ? isInvestmentCompanyInSearch(investmentData, name)
      : false
    const isCompanyRowWithMatchingOfferingName = investment.type === 'company'
      ? doesCompanyOfferingHaveInvestmentOfferingName(investmentData, name)
      : false
    const matchesOfferingName = investment.type !== 'company' ? investment.offeringName.toLowerCase().includes(name.toLowerCase()) : false
    return matchesInvestmentName || matchesCompanyName || matchesOfferingName || isCompanyRowWithMatchingInvestments || isCompanyRowWithMatchingOfferingName
  })
}

export const filterInvestmentsByStatus = (investmentData, investmentFilter) => {
  return investmentData.filter(i => {
    if (investmentFilter === 'all') {
      return i.status !== ''
    }
    if (investmentFilter === 'com') {
      return i.type === 'company' || i.status === 'complete' || i.status === 'counter signed'
    }
    if (investmentFilter === 'inc') {
      return i.status !== 'complete' && i.status !== 'counter signed' && i.status !== ''
    }
    return i.status === investmentFilter
  })
}

export const calculateCompanyTotals = (investmentData, totalsByCompany) => {
  return investmentData.reduce((investments, investment) => {
    if (investment.type === 'company' && totalsByCompany[investment.issuerName]) {
      return [
        ...investments,
        {
        ...investment,
        number_of_units: totalsByCompany[investment.issuerName].totalShares,
        amount: totalsByCompany[investment.issuerName].totalInvestments
      }
    ]
    } else {
      return investment.isVisible ? [ ...investments, investment ] : investments
    }
  }, []);
}

export const createTotalRow = (totalsByCompany) => {
  const totalsForTable = Object.keys(totalsByCompany).reduce((totalsForTable, companyName) => {
    const companyTotalData = totalsByCompany[companyName]
    return {
      totalInvestments: totalsForTable.totalInvestments + companyTotalData.totalInvestments,
      totalShares: totalsForTable.totalShares + companyTotalData.totalShares,
    }
  }, { totalInvestments: 0, totalShares: 0 })
  return {
    issuerName: "TOTAL",
    amount: totalsForTable.totalInvestments,
    number_of_units: totalsForTable.totalShares,
    type: 'total'
  }
}

export const sortDocumentsByTag = (documentsData, investment) => {
  // TODO: remove irrelevant tags when company docs are shown in its own row
  const tags = ['offering-doc', 'subscription', 'tax-form', 'updates', 'other', 'signed-agreement'];
  let documentsByTag = {};
  for (let tag in tags) {
    const docs = documentsData.filter(document => document.tag === tags[tag]);
    if(docs.length !== 0) {
      documentsByTag = { ...documentsByTag, [tags[tag]]: {'documents': docs}};
    }
  }
  if (investment && investment.hasSignedDocument === true) {
    documentsByTag['signed-agreement'] = {
      'documents': [{
        name: 'Signed Document',
        created_at: investment.closed_on,
        type: 'signedAgreement',
        investment_id: investment.id
      }]
    }
  }

  return documentsByTag;
}

export const transformDocumentsByTagForTable = (documentsByTag, expandedTags) => {
  const rows = Object.keys(documentsByTag).reduce((rows, tagName) => {
    const tagRow = {
      tag: tagName, 
      type: 'tag',
    }
    const documentsRow = expandedTags[tagName] ? documentsByTag[tagName].documents.map(document => ({
      ...document,
    })) : []
    return [...rows, tagRow, ...documentsRow]
  }, [])
  return rows
}